import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Play } from '@phosphor-icons/react';
import { Slide } from '../Slide';
import { InView } from '../InView';
import { SlideHeadline } from '../SlideHeadline';
import { EmbeddedSnakWindow } from '../EmbeddedSnakWindow';
import { attentionGrabberImage, bondingSnakUrl, screenshots } from '../../../data/deck';

/** Crop away the screenshot's own heading so only the six examples remain. */
const CROP_TOP = 335;
const SOURCE = {
  width: 2576,
  height: 1271
};
const VISIBLE_HEIGHT = SOURCE.height - CROP_TOP;

/** The "Attention grabber" phone screen, in source pixels. */
const GRABBER = {
  x: 910,
  y: 372,
  width: 252,
  height: 504
};
const pct = (value: number, total: number) => `${value / total * 100}%`;
export function UseCasesSlide() {
  const [playing, setPlaying] = useState(false);
  return <Slide id="useCases" data-id="element-446" data-mp="1df7z6yczx8u3">
      <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16" data-id="element-447" data-mp="18etpzcznrsuh">
        <InView data-id="element-448" data-mp="qtjls80tt2uc">
          <SlideHeadline lead="One format." follow="Many use cases." data-id="element-449" data-mp="qu2ucy4l6evp" />
        </InView>
        <InView delay={0.1} data-id="element-450" data-mp="vruwsm2uxje5">
          <p className="font-body text-[14.5px] leading-[1.7] text-[rgba(1,51,43,0.65)]" data-id="element-451" data-mp="vt60klwayr59">
            The format that introduces the Filtek family can also do other jobs: research,
            door openers, study updates, email outreach and congress activation.
          </p>
        </InView>
      </div>

      <InView delay={0.18} y={26} className="mt-14" data-id="element-452" data-mp="19j9empl13ejm">
        <figure className="overflow-hidden rounded-[28px] bg-[#FAF8F4] p-4 ring-1 ring-[rgba(1,51,43,0.08)] sm:p-6" data-id="element-453" data-mp="1eim29cjq4pn9">
          <div className="relative overflow-hidden" style={{
          aspectRatio: `${SOURCE.width} / ${VISIBLE_HEIGHT}`
        }} data-id="element-454" data-mp="1jhp8h0bfx1wt">
            <img src={screenshots.snakUseCases} alt="Six Snak examples: market research pulse, door opener, attention grabber, quick study update, email outreach and congress activation" className="block w-full" style={{
            marginTop: `-${CROP_TOP / SOURCE.width * 100}%`
          }} data-id="element-455" data-mp="1oez26s5v3zqg" />

            <button type="button" onClick={() => setPlaying(true)} aria-label="Attention grabber — start the bonding Snak" className="group absolute overflow-hidden rounded-[8%/4%] bg-[#14332B] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" style={{
            left: pct(GRABBER.x, SOURCE.width),
            top: pct(GRABBER.y - CROP_TOP, VISIBLE_HEIGHT),
            width: pct(GRABBER.width, SOURCE.width),
            height: pct(GRABBER.height, VISIBLE_HEIGHT)
          }} data-id="element-456" data-mp="3d9fhrh2xw89q">
              <img src={attentionGrabberImage} alt="Bonding Snak screen: 'Your peers agree — more time for the moments that matter'" className="block h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.03]" data-id="element-457" data-mp="9symjijtzvt5" />
              <motion.span aria-hidden="true" className="absolute inset-0 rounded-[inherit] ring-2 ring-primary" animate={{
              opacity: [0.9, 0.2, 0.9]
            }} transition={{
              repeat: Infinity,
              duration: 2,
              ease: 'easeInOut'
            }} data-id="element-458" data-mp="12fov3n4i4mq3" />
              <span className="absolute bottom-[5%] left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-[#0B1512] px-3 py-1.5 font-body text-[clamp(9px,0.9vw,13px)] font-medium text-white shadow-lg" data-id="element-459" data-mp="ybh4tlieaypr">
                <Play size={11} weight="fill" aria-hidden="true" data-id="element-460" data-mp="oggwowg5ximy" />
                Start Snak
              </span>
            </button>
          </div>
        </figure>
      </InView>

      <InView delay={0.3} data-id="element-461" data-mp="ep2nty2bn6h5">
        <p className="mt-14 max-w-[40ch] border-t border-[rgba(1,51,43,0.12)] pt-8 font-heading text-[26px] leading-[1.12] tracking-[-0.02em] text-background sm:text-[32px]" data-id="element-462" data-mp="p1lee6erqzk">
          The value of a Snak is not only who sees it —{' '}
          <span className="text-[rgba(1,51,43,0.45)]" data-id="element-463" data-mp="180hk7v730crp">but what barrier it helps remove.</span>
        </p>
      </InView>

      <AnimatePresence data-id="element-464" data-mp="140h4864c3tij">
        {playing && <EmbeddedSnakWindow key="bonding" url={bondingSnakUrl} title="Attention grabber Snak" onClose={() => setPlaying(false)} data-id="element-465" data-mp="z8z5zptp4719" />}
      </AnimatePresence>
    </Slide>;
}